import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeTwoToneIcon from "@mui/icons-material/BadgeTwoTone";
import BusinessTwoToneIcon from "@mui/icons-material/BusinessTwoTone";
import CalendarTodayTwoToneIcon from "@mui/icons-material/CalendarTodayTwoTone";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import HomeTwoToneIcon from "@mui/icons-material/HomeTwoTone";
import PersonTwoToneIcon from "@mui/icons-material/PersonTwoTone";
import PhoneTwoToneIcon from "@mui/icons-material/PhoneTwoTone";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import {
	alpha,
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Container,
	Divider,
	Grid,
	IconButton,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	Tooltip,
	Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import GlobalLoader from "src/components/SuspenseLoader/GlobalLoader";
import { useApiQuery } from "src/hooks/useApiQuery";
import DeleteTicketModal from "../TicketScan/DeleteTicketModal";
import TicketWiseScanModal from "../TicketScan/TicketWiseScanModal";
import UpdateTicketModal from "../TicketScan/UpdateTicketModal";
import type { IVisitorDetails, IVisitorTicket } from "../type/newVisitorType";
import UpdateVisitor from "./UpdateVisitor";


const getStatusChip = (status: string) => {
	const normalized = (status || "").toLowerCase();
	if (normalized === "approved" || normalized === "paid") {
		return <Chip label={status || "Approved"} color="success" size="small" sx={{ fontWeight: 600 }} />;
	}
	if (normalized.includes("pending")) {
		return <Chip label={status || "Pending"} color="warning" size="small" sx={{ fontWeight: 600 }} />;
	}
	if (normalized === "rejected" || normalized === "unpaid") {
		return <Chip label={status || "Rejected"} color="error" size="small" sx={{ fontWeight: 600 }} />;
	}
	return <Chip label={status || "Active"} color="default" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
};

const SingleVisitorPage: React.FC = () => {
	const { id, singleVisitorId } = useParams<{
		id?: string;
		singleVisitorId?: string;
	}>();
	const visitorId = Number(id || singleVisitorId);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const urlTab = searchParams.get("tab");
	const [activeTab, setActiveTab] = useState<string>(() => {
		if (urlTab === "tickets" || urlTab === "scans") {
			return urlTab;
		}
		const savedTab = localStorage.getItem("visitor_active_tab");
		if (savedTab === "tickets" || savedTab === "scans") {
			return savedTab;
		}
		return "tickets";
	});

	useEffect(() => {
		if (urlTab && (urlTab === "tickets" || urlTab === "scans")) {
			setActiveTab(urlTab);
			localStorage.setItem("visitor_active_tab", urlTab);
		} else {
			const savedTab = localStorage.getItem("visitor_active_tab");
			if (savedTab === "tickets" || savedTab === "scans") {
				setActiveTab(savedTab);
				setSearchParams({ tab: savedTab }, { replace: true });
			}
		}
	}, [urlTab, setSearchParams]);

	const handleTabChange = (_event: React.SyntheticEvent, newTab: string) => {
		setActiveTab(newTab);
		localStorage.setItem("visitor_active_tab", newTab);
		setSearchParams({ tab: newTab });
	};

	const [openEditModal, setOpenEditModal] = useState<boolean>(false);
	const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
	const [scanModalState, setScanModalState] = useState<{
		open: boolean;
		ticket: IVisitorTicket | null;
	}>({
		open: false,
		ticket: null,
	});
	const [updateTicketModal, setUpdateTicketModal] = useState<{
		open: boolean;
		ticket: IVisitorTicket | null;
	}>({
		open: false,
		ticket: null,
	});
	const [deleteTicketModal, setDeleteTicketModal] = useState<{
		open: boolean;
		ticket: IVisitorTicket | null;
	}>({
		open: false,
		ticket: null,
	});


	const { data: visitor, isLoading } = useApiQuery<IVisitorDetails>({
		endPoint: `/api/v1/admin/visitor/${visitorId}`,
		queryKeys: ["VisitorDetails", visitorId],
		enabled: Boolean(visitorId),
	});

	if (isLoading && !visitor) {
		return <GlobalLoader />;
	}

	const fullName =
		(
			visitor?.visitor_name ||
			`${visitor?.visitor_name || ""}`
		).trim() || "Visitor Details";

	const ticketsList = visitor?.tickets || [];
	const scansList = visitor?.scans || [];

	return (
		<>
			<Helmet>
				<title>
					{visitor ? `BTTF - ${fullName}` : "BTTF - Visitor Details"}
				</title>
			</Helmet>

			<Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
				{/* Top Navigation Bar */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 3,
						flexWrap: "wrap",
						gap: 2,
					}}
				>
					<Button
						variant="outlined"
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate("/visitor/list")}
						sx={{
							borderColor: "grey.400",
							color: "text.primary",
							"&:hover": { backgroundColor: "grey.100" },
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
						}}
					>
						Back to Visitor List
					</Button>

					{visitor && (
						<Box sx={{ display: "flex", gap: 1.5 }}>
							<Button
								variant="contained"
								color="primary"
								startIcon={<EditTwoToneIcon />}
								onClick={() => setOpenEditModal(true)}
								sx={{
									borderRadius: 2,
									textTransform: "none",
									fontWeight: 600,
									boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
								}}
							>
								Edit Visitor
							</Button>
							{/* <Button
								variant="outlined"
								color="error"
								startIcon={<DeleteTwoToneIcon />}
								onClick={() => setOpenDeleteModal(true)}
								sx={{
									borderRadius: 2,
									textTransform: "none",
									fontWeight: 600,
								}}
							>
								Delete
							</Button> */}
						</Box>
					)}
				</Box>

				{!visitor ? (
					<Card sx={{ p: 5, textAlign: "center" }}>
						<PersonTwoToneIcon
							sx={{ fontSize: 54, color: "text.disabled", mb: 1 }}
						/>
						<Typography variant="h4" color="text.secondary">
							Visitor not found or has been removed.
						</Typography>
					</Card>
				) : (
					<Grid container spacing={3}>
						{/* Overview Info Card */}
						<Grid item xs={12}>
							<Card elevation={2} sx={{ overflow: "hidden", borderRadius: 2 }}>
								<CardHeader
									sx={{
										bgcolor: (theme) =>
											alpha(theme.palette.primary.main, 0.03),
										py: 2.5,
										px: 3,
									}}
									title={
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												flexWrap: "wrap",
												gap: 2,
											}}
										>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 2,
												}}
											>
												<Avatar
													src={visitor.visitor_photo || undefined}
													sx={{
														width: 54,
														height: 54,
														bgcolor: "primary.main",
														fontSize: "1.3rem",
														fontWeight: 700,
														boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
													}}
												>
													{fullName.charAt(0).toUpperCase()}
												</Avatar>
												<Box>
													<Typography variant="h3" fontWeight={700}>
														{fullName}
													</Typography>
													{visitor.visitor_organization && (
														<Typography
															variant="body2"
															color="text.secondary"
															sx={{ mt: 0.2 }}
														>
															{visitor.visitor_designation
																? `${visitor.visitor_designation}, ${visitor.visitor_organization}`
																: visitor.visitor_organization}
														</Typography>
													)}
												</Box>
											</Box>

											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												{visitor.visitor_no && (
													<Chip
														label={`No: ${visitor.visitor_no}`}
														color="primary"
														variant="filled"
														sx={{
															fontWeight: 700,
															borderRadius: 2,
															px: 0.5,
														}}
													/>
												)}
												{visitor.visitor_status &&
													getStatusChip(visitor.visitor_status)}
											</Box>
										</Box>
									}
								/>
								<Divider />
								<CardContent sx={{ p: 3 }}>
									<Grid container spacing={3}>
										{/* Email */}
										<Grid item xs={12} sm={6} md={3}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box
													sx={{
														p: 1,
														borderRadius: 2,
														bgcolor: (theme) =>
															alpha(theme.palette.info.main, 0.1),
														color: "info.main",
														display: "flex",
														flexShrink: 0,
													}}
												>
													<EmailTwoToneIcon fontSize="small" />
												</Box>
												<Box sx={{ minWidth: 0, flexGrow: 1 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														display="block"
													>
														Email Address
													</Typography>
													<Typography
														variant="body2"
														fontWeight={600}
														noWrap
														title={visitor.visitor_email || ""}
													>
														{visitor.visitor_email || "N/A"}
													</Typography>
												</Box>
											</Box>
										</Grid>

										{/* Phone */}
										<Grid item xs={12} sm={6} md={3}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box
													sx={{
														p: 1,
														borderRadius: 2,
														bgcolor: (theme) =>
															alpha(theme.palette.success.main, 0.1),
														color: "success.main",
														display: "flex",
														flexShrink: 0,
													}}
												>
													<PhoneTwoToneIcon fontSize="small" />
												</Box>
												<Box sx={{ minWidth: 0, flexGrow: 1 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														display="block"
													>
														Phone Number
													</Typography>
													<Typography
														variant="body2"
														fontWeight={600}
														noWrap
														title={visitor.visitor_phone || ""}
													>
														{visitor.visitor_phone || "N/A"}
													</Typography>
												</Box>
											</Box>
										</Grid>

										{/* Gender */}
										<Grid item xs={12} sm={6} md={3}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box
													sx={{
														p: 1,
														borderRadius: 2,
														bgcolor: (theme) =>
															alpha(theme.palette.secondary.main, 0.1),
														color: "secondary.main",
														display: "flex",
														flexShrink: 0,
													}}
												>
													<PersonTwoToneIcon fontSize="small" />
												</Box>
												<Box sx={{ minWidth: 0, flexGrow: 1 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														display="block"
													>
														Gender
													</Typography>
													<Typography
														variant="body2"
														fontWeight={600}
														textTransform="capitalize"
														noWrap
													>
														{visitor.visitor_gender || "N/A"}
													</Typography>
												</Box>
											</Box>
										</Grid>

										{/* Registered Date */}
										<Grid item xs={12} sm={6} md={3}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box
													sx={{
														p: 1,
														borderRadius: 2,
														bgcolor: (theme) =>
															alpha(theme.palette.info.main, 0.1),
														color: "info.main",
														display: "flex",
														flexShrink: 0,
													}}
												>
													<CalendarTodayTwoToneIcon fontSize="small" />
												</Box>
												<Box sx={{ minWidth: 0, flexGrow: 1 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														display="block"
													>
														Registered At
													</Typography>
													<Typography
														variant="body2"
														fontWeight={600}
														noWrap
													>
														{visitor.visitor_created_at
															? moment(visitor.visitor_created_at).format("LLL")
															: "N/A"}
													</Typography>
												</Box>
											</Box>
										</Grid>

										{/* Organization */}
										<Grid item xs={12} sm={6} md={3}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box
													sx={{
														p: 1,
														borderRadius: 2,
														bgcolor: (theme) =>
															alpha(theme.palette.warning.main, 0.1),
														color: "warning.main",
														display: "flex",
														flexShrink: 0,
													}}
												>
													<BusinessTwoToneIcon fontSize="small" />
												</Box>
												<Box sx={{ minWidth: 0, flexGrow: 1 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														display="block"
													>
														Organization
													</Typography>
													<Typography
														variant="body2"
														fontWeight={600}
														noWrap
														title={visitor.visitor_organization || ""}
													>
														{visitor.visitor_organization || "N/A"}
													</Typography>
												</Box>
											</Box>
										</Grid>

										{/* Designation */}
										<Grid item xs={12} sm={6} md={3}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box
													sx={{
														p: 1,
														borderRadius: 2,
														bgcolor: (theme) =>
															alpha(theme.palette.primary.main, 0.1),
														color: "primary.main",
														display: "flex",
														flexShrink: 0,
													}}
												>
													<BadgeTwoToneIcon fontSize="small" />
												</Box>
												<Box sx={{ minWidth: 0, flexGrow: 1 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														display="block"
													>
														Designation
													</Typography>
													<Typography
														variant="body2"
														fontWeight={600}
														noWrap
														title={visitor.visitor_designation || ""}
													>
														{visitor.visitor_designation || "N/A"}
													</Typography>
												</Box>
											</Box>
										</Grid>

										{/* Address */}
										<Grid item xs={12} sm={12} md={6}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box
													sx={{
														p: 1,
														borderRadius: 2,
														bgcolor: (theme) =>
															alpha(theme.palette.success.main, 0.1),
														color: "success.main",
														display: "flex",
														flexShrink: 0,
													}}
												>
													<HomeTwoToneIcon fontSize="small" />
												</Box>
												<Box sx={{ minWidth: 0, flexGrow: 1 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														display="block"
													>
														Address
													</Typography>
													<Typography
														variant="body2"
														fontWeight={600}
														noWrap
														title={visitor.visitor_address || ""}
													>
														{visitor.visitor_address || "N/A"}
													</Typography>
												</Box>
											</Box>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Grid>

						{/* Segmented Tabs Section */}
						<Grid item xs={12}>
							<Card elevation={2} sx={{ overflow: "hidden", borderRadius: 2 }}>
								<Box
									sx={{
										p: 2,
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										flexWrap: "wrap",
										gap: 2,
										borderBottom: "1px solid",
										borderColor: "divider",
										bgcolor: "#fcfcfd",
									}}
								>
									{/* Contiguous Segmented Tab Container ("gaye gaye lege thakbe") */}
									<Box
										sx={{
											p: 0.5,
											bgcolor: "#eef2f6",
											borderRadius: 2.5,
											display: "inline-flex",
											border: "1px solid #e2e8f0",
										}}
									>
										<Tabs
											value={activeTab}
											onChange={handleTabChange}
											TabIndicatorProps={{
												style: { display: "none" },
											}}
											sx={{
												minHeight: 38,
												"& .MuiTabs-flexContainer": {
													gap: 0,
												},
												"& .MuiTab-root": {
													minHeight: 38,
													textTransform: "none",
													borderRadius: 2,
													px: 3,
													py: 0.8,
													color: "#475569",
													fontWeight: 600,
													fontSize: "0.92rem",
													transition: "all 0.2s ease-in-out",
													"&:hover": {
														color: "primary.main",
														bgcolor: "rgba(255, 255, 255, 0.6)",
													},
													"&.Mui-selected": {
														bgcolor: "primary.main",
														color: "#ffffff !important",
														fontWeight: 700,
														boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
													},
												},
											}}
										>
											<Tab
												value="tickets"
												icon={
													<ConfirmationNumberTwoToneIcon
														fontSize="small"
														sx={{
															color: activeTab === "tickets" ? "#ffffff" : "inherit",
														}}
													/>
												}
												iconPosition="start"
												label={
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<span>Tickets</span>
														<Chip
															label={ticketsList.length}
															size="small"
															sx={{
																height: 20,
																fontSize: "0.75rem",
																fontWeight: 700,
																bgcolor:
																	activeTab === "tickets"
																		? "rgba(255, 255, 255, 0.25)"
																		: "#e2e8f0",
																color: activeTab === "tickets" ? "#ffffff" : "#475569",
															}}
														/>
													</Box>
												}
											/>
											<Tab
												value="scans"
												icon={
													<QrCodeScannerTwoToneIcon
														fontSize="small"
														sx={{
															color: activeTab === "scans" ? "#ffffff" : "inherit",
														}}
													/>
												}
												iconPosition="start"
												label={
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<span>Scans History</span>
														<Chip
															label={scansList.length}
															size="small"
															sx={{
																height: 20,
																fontSize: "0.75rem",
																fontWeight: 700,
																bgcolor:
																	activeTab === "scans"
																		? "rgba(255, 255, 255, 0.25)"
																		: "#e2e8f0",
																color: activeTab === "scans" ? "#ffffff" : "#475569",
															}}
														/>
													</Box>
												}
											/>
										</Tabs>
									</Box>
								</Box>

								{/* Tab Panel 1: Tickets */}
								{activeTab === "tickets" && (
									<TableContainer>
										<Table>
											<TableHead>
												<TableRow sx={{ bgcolor: "#f8fafc" }}>
													<TableCell sx={{ fontWeight: "bold", width: 60 }}>SL.</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Ticket Info</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Quantity & Pricing</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Payment Details</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Scans</TableCell>
													<TableCell sx={{ fontWeight: "bold" }} align="center">
														Action
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{ticketsList.length > 0 ? (
													ticketsList.map((ticket, index) => (
														<TableRow hover key={ticket.id || index}>
															<TableCell>{index + 1}</TableCell>
															<TableCell>
																<Chip
																	component={RouterLink}
																	to={`/visitor/ticket/${ticket.id}`}
																	clickable
																	label={ticket.ticket_no || `#${ticket.id}`}
																	size="small"
																	color="primary"
																	variant="outlined"
																	sx={{ fontWeight: 600, mb: 0.3, cursor: "pointer" }}
																/>
																{ticket.created_at && (
																	<Typography
																		variant="caption"
																		color="text.secondary"
																		sx={{ display: "block", fontSize: "0.75rem" }}
																	>
																		Created: {moment(ticket.created_at).format("ll")}
																	</Typography>
																)}
															</TableCell>
															<TableCell>
																<Typography
																	variant="body2"
																	fontWeight={700}
																	color="primary.main"
																>
																	{ticket.total_amount
																		? `৳${ticket.total_amount}`
																		: "-"}
																</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary"
																	sx={{ display: "block" }}
																>
																	Qty: {ticket.quantity || 1}
																	{ticket.item_amount
																		? ` (৳${ticket.item_amount}/pc)`
																		: ""}
																</Typography>
															</TableCell>
															<TableCell>
																<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
																	{getStatusChip(ticket.payment_status)}
																	{ticket.payment_method && (
																		<Typography
																			variant="caption"
																			fontWeight={600}
																			textTransform="capitalize"
																			color="text.primary"
																		>
																			{ticket.payment_method}
																		</Typography>
																	)}
																</Box>
																{ticket.payment_details?.note && (
																	<Typography
																		variant="caption"
																		color="text.secondary"
																		sx={{ display: "block", fontSize: "0.75rem" }}
																	>
																		Note: {ticket.payment_details.note}
																	</Typography>
																)}
															</TableCell>
															<TableCell>
																<Chip
																	label={`${ticket.scans?.length || 0} scan(s)`}
																	size="small"
																	color="info"
																	variant="outlined"
																	sx={{ fontWeight: 600 }}
																/>
															</TableCell>
															<TableCell align="center">
																<Box
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		gap: 0.8,
																	}}
																>
																	<Tooltip title="View Ticket Details" arrow>
																		<IconButton
																			component={RouterLink}
																			to={`/visitor/ticket/${ticket.id}`}
																			size="small"
																			color="primary"
																			sx={{
																				p: 0.7,
																				borderRadius: 1.5,
																				border: (theme) =>
																					`1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
																				bgcolor: (theme) =>
																					alpha(theme.palette.primary.main, 0.05),
																				"&:hover": {
																					bgcolor: (theme) =>
																						alpha(theme.palette.primary.main, 0.15),
																				},
																			}}
																		>
																			<VisibilityTwoToneIcon sx={{ fontSize: 16 }} />
																		</IconButton>
																	</Tooltip>

																	<Tooltip title="Update Ticket" arrow>
																		<IconButton
																			size="small"
																			color="primary"
																			onClick={() =>
																				setUpdateTicketModal({
																					open: true,
																					ticket,
																				})
																			}
																			sx={{
																				p: 0.7,
																				borderRadius: 1.5,
																				border: (theme) =>
																					`1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
																				bgcolor: (theme) =>
																					alpha(theme.palette.primary.main, 0.05),
																				"&:hover": {
																					bgcolor: (theme) =>
																						alpha(theme.palette.primary.main, 0.15),
																				},
																			}}
																		>
																			<EditTwoToneIcon sx={{ fontSize: 16 }} />
																		</IconButton>
																	</Tooltip>

																	<Button
																		size="small"
																		variant="outlined"
																		color="primary"
																		startIcon={<QrCodeScannerTwoToneIcon />}
																		onClick={() =>
																			setScanModalState({
																				open: true,
																				ticket,
																			})
																		}
																		sx={{
																			borderRadius: 1.5,
																			textTransform: "none",
																			fontWeight: 600,
																			whiteSpace: "nowrap",
																		}}
																	>
																		Scan Ticket
																	</Button>

																	<Tooltip title="Delete Ticket" arrow>
																		<IconButton
																			size="small"
																			color="error"
																			onClick={() =>
																				setDeleteTicketModal({
																					open: true,
																					ticket,
																				})
																			}
																			sx={{
																				p: 0.7,
																				borderRadius: 1.5,
																				border: (theme) =>
																					`1px solid ${alpha(theme.palette.error.main, 0.3)}`,
																				bgcolor: (theme) =>
																					alpha(theme.palette.error.main, 0.05),
																				"&:hover": {
																					bgcolor: (theme) =>
																						alpha(theme.palette.error.main, 0.15),
																				},
																			}}
																		>
																			<DeleteTwoToneIcon sx={{ fontSize: 16 }} />
																		</IconButton>
																	</Tooltip>
																</Box>
															</TableCell>
														</TableRow>
													))
												) : (
													<TableRow>
														<TableCell colSpan={6} align="center" sx={{ py: 5 }}>
															<Typography variant="body2" color="text.secondary">
																No tickets found for this visitor.
															</Typography>
														</TableCell>
													</TableRow>
												)}
											</TableBody>
										</Table>
									</TableContainer>
								)}

								{/* Tab Panel 2: Scans History */}
								{activeTab === "scans" && (
									<TableContainer>
										<Table>
											<TableHead>
												<TableRow sx={{ bgcolor: "#f8fafc" }}>
													<TableCell sx={{ fontWeight: "bold", width: 60 }}>SL.</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Scan Time</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Ticket No</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Scan Type</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Scanned By</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{scansList.length > 0 ? (
													scansList.map((scan, index) => (
														<TableRow hover key={scan.id || index}>
															<TableCell>{index + 1}</TableCell>
															<TableCell>
																<Typography variant="body2" fontWeight={500}>
																	{scan.scan_time
																		? moment(scan.scan_time).format("LLL")
																		: "-"}
																</Typography>
															</TableCell>
															<TableCell>
																<Chip
																	label={scan.ticket_no || `#${scan.ticket_id}`}
																	size="small"
																	color="primary"
																	variant="outlined"
																	sx={{ fontWeight: 600 }}
																/>
															</TableCell>
															<TableCell>
																<Chip
																	label={scan.scan_type || "IN"}
																	size="small"
																	color={scan.scan_type === "IN" ? "success" : "info"}
																	variant="filled"
																	sx={{ fontWeight: 600 }}
																/>
															</TableCell>
															<TableCell>
																<Typography variant="body2" fontWeight={600}>
																	{scan.scan_by_name || "-"}
																</Typography>
																{scan.scan_by_email && (
																	<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
																		{scan.scan_by_email}
																	</Typography>
																)}
															</TableCell>
															<TableCell>
																<Typography variant="body2" fontWeight={700} color="primary.main">
																	{scan.total_amount ? `৳${scan.total_amount}` : "-"}
																</Typography>
															</TableCell>
														</TableRow>
													))
												) : (
													<TableRow>
														<TableCell colSpan={6} align="center" sx={{ py: 5 }}>
															<Typography variant="body2" color="text.secondary">
																No scan history records found for this visitor.
															</Typography>
														</TableCell>
													</TableRow>
												)}
											</TableBody>
										</Table>
									</TableContainer>
								)}
							</Card>
						</Grid>
					</Grid>
				)}

				{/* Modals */}
				<UpdateVisitor
					open={openEditModal}
					handleClose={() => setOpenEditModal(false)}
					visitorId={visitorId}
				/>

				{/* <DeleteVisitor
					open={openDeleteModal}
					handleClose={() => {
						setOpenDeleteModal(false);
						navigate("/visitor/list");
					}}
					visitorId={visitorId}
					visitorName={fullName}
				/> */}

				<TicketWiseScanModal
					open={scanModalState.open}
					handleClose={() =>
						setScanModalState({
							open: false,
							ticket: null,
						})
					}
					ticket={scanModalState.ticket}
					visitorId={visitorId}
				/>

				{/* Update Ticket Modal */}
				{updateTicketModal.open && (
					<UpdateTicketModal
						open={updateTicketModal.open}
						ticket={updateTicketModal.ticket}
						handleClose={() =>
							setUpdateTicketModal({
								open: false,
								ticket: null,
							})
						}
					/>
				)}

				{/* Delete Ticket Modal */}
				{deleteTicketModal.open && (
					<DeleteTicketModal
						open={deleteTicketModal.open}
						ticket={deleteTicketModal.ticket}
						handleClose={() =>
							setDeleteTicketModal({
								open: false,
								ticket: null,
							})
						}
					/>
				)}
			</Container>
		</>
	);
};

export default SingleVisitorPage;
